import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Coffee, Building2, BookOpen, Beer, BriefcaseBusiness, Wifi, Plug, Star, ArrowRight, ChevronRight, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import VenueCard from "../components/VenueCard";
import VenueMap from "../components/VenueMap";
import SEO, { BreadcrumbStructuredData } from "../components/SEO";
import { trackPageView } from "../utils/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fallback city data (used if API data not available)
const FALLBACK_CITIES = {
  london: { name: "London", lat: 51.5074, lng: -0.1278, description: "The UK's capital offers endless options for remote workers.", highlights: ["Shoreditch tech hubs", "British Library"] },
  manchester: { name: "Manchester", lat: 53.4808, lng: -2.2426, description: "A thriving digital hub in the North.", highlights: ["Northern Quarter cafes", "MediaCityUK"] },
  birmingham: { name: "Birmingham", lat: 52.4862, lng: -1.8904, description: "The UK's second city has transformed into a remote work haven.", highlights: ["Jewellery Quarter", "Library of Birmingham"] },
  edinburgh: { name: "Edinburgh", lat: 55.9533, lng: -3.1883, description: "Scotland's capital combines historic charm with modern workspaces.", highlights: ["Old Town cafes", "National Library"] },
  bristol: { name: "Bristol", lat: 51.4545, lng: -2.5879, description: "A creative city with vibrant cafe culture.", highlights: ["Stokes Croft", "Harbourside venues"] },
  leeds: { name: "Leeds", lat: 53.8008, lng: -1.5491, description: "Yorkshire's largest city offers great value workspaces.", highlights: ["Leeds Dock", "City centre libraries"] },
  glasgow: { name: "Glasgow", lat: 55.8642, lng: -4.2518, description: "Scotland's largest city with modern coworking spaces.", highlights: ["Merchant City", "West End cafes"] },
  liverpool: { name: "Liverpool", lat: 53.4084, lng: -2.9916, description: "A city with rich cultural heritage.", highlights: ["Baltic Triangle", "Albert Dock"] },
  cardiff: { name: "Cardiff", lat: 51.4816, lng: -3.1791, description: "Wales' capital with excellent workspaces.", highlights: ["Cardiff Bay", "City centre"] },
  brighton: { name: "Brighton", lat: 50.8225, lng: -0.1372, description: "A creative seaside city.", highlights: ["North Laine", "Seafront cafes"] },
  oxford: { name: "Oxford", lat: 51.7520, lng: -1.2577, description: "The city of dreaming spires.", highlights: ["Bodleian Library", "Jericho cafes"] },
  cambridge: { name: "Cambridge", lat: 52.2053, lng: 0.1218, description: "A university city with quiet cafes.", highlights: ["University Library", "Mill Road cafes"] },
  nottingham: { name: "Nottingham", lat: 52.9548, lng: -1.1581, description: "A student-friendly city.", highlights: ["Hockley", "Lace Market"] },
  sheffield: { name: "Sheffield", lat: 53.3811, lng: -1.4701, description: "Known for its friendly atmosphere.", highlights: ["Kelham Island", "Division Street"] },
  newcastle: { name: "Newcastle", lat: 54.9783, lng: -1.6178, description: "A vibrant northern city.", highlights: ["Ouseburn", "Quayside"] }
};

const venueTypeIcons = {
  cafe: Coffee,
  pub: Beer,
  hotel: Building2,
  library: BookOpen,
  hub: BriefcaseBusiness
};

// Proper plural forms for venue types
const venueTypePlurals = {
  cafe: "Cafes",
  pub: "Pubs",
  hotel: "Hotels",
  library: "Libraries",
  hub: "Hubs",
  other: "Others"
};

export default function CityPage() {
  const { citySlug } = useParams();
  const [venues, setVenues] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);
  const [cityInfo, setCityInfo] = useState(null);
  const [allCities, setAllCities] = useState([]);

  const showCityIndex = !citySlug;

  // Memoize filtered other cities to avoid recalculation on every render
  const otherCities = useMemo(() => 
    Object.entries(FALLBACK_CITIES)
      .filter(([slug]) => slug !== citySlug?.toLowerCase())
      .slice(0, 8),
    [citySlug]
  );

  useEffect(() => {
    if (citySlug) {
      fetchCityData();
      trackPageView(`/city/${citySlug}`);
    } else {
      fetchAllCities();
      trackPageView('/city');
    }
  }, [citySlug]);

  const fetchAllCities = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cities`);
      if (response.data && response.data.length > 0) {
        setAllCities(response.data);
      } else {
        // Fallback to hardcoded data
        setAllCities(Object.entries(FALLBACK_CITIES).map(([slug, data]) => ({ slug, ...data })));
      }
    } catch (error) {
      // Fallback to hardcoded data
      setAllCities(Object.entries(FALLBACK_CITIES).map(([slug, data]) => ({ slug, ...data })));
    } finally {
      setLoading(false);
    }
  };

  const fetchCityData = async () => {
    setLoading(true);
    try {
      // Try to get city info from API first
      const cityResponse = await axios.get(`${API}/cities/${citySlug}`);
      if (cityResponse.data) {
        setCityInfo(cityResponse.data);
      } else {
        // Fallback to hardcoded data
        const fallback = FALLBACK_CITIES[citySlug.toLowerCase()];
        if (fallback) {
          setCityInfo({ slug: citySlug, ...fallback });
        }
      }
    } catch (error) {
      // Fallback to hardcoded data
      const fallback = FALLBACK_CITIES[citySlug.toLowerCase()];
      if (fallback) {
        setCityInfo({ slug: citySlug, ...fallback });
      }
    }

    // Fetch venues for this city
    try {
      const cityName = cityInfo?.name || FALLBACK_CITIES[citySlug.toLowerCase()]?.name || citySlug;
      const response = await axios.get(`${API}/venues`, {
        params: { city: cityName, limit: 50 }
      });
      setVenues(response.data);

      // Calculate stats
      const typeStats = {};
      let totalRating = 0;
      let ratingCount = 0;
      
      response.data.forEach(v => {
        typeStats[v.venue_type] = (typeStats[v.venue_type] || 0) + 1;
        if (v.rating) {
          totalRating += v.rating;
          ratingCount++;
        }
      });

      setStats({
        total: response.data.length,
        byType: typeStats,
        avgRating: ratingCount > 0 ? (totalRating / ratingCount).toFixed(1) : null
      });
    } catch (error) {
      console.error("Error fetching venues:", error);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch venues when cityInfo changes
  useEffect(() => {
    if (cityInfo && citySlug) {
      const fetchVenues = async () => {
        try {
          const response = await axios.get(`${API}/venues`, {
            params: { city: cityInfo.name, limit: 50 }
          });
          setVenues(response.data);

          const typeStats = {};
          let totalRating = 0;
          let ratingCount = 0;
          
          response.data.forEach(v => {
            typeStats[v.venue_type] = (typeStats[v.venue_type] || 0) + 1;
            if (v.rating) {
              totalRating += v.rating;
              ratingCount++;
            }
          });

          setStats({
            total: response.data.length,
            byType: typeStats,
            avgRating: ratingCount > 0 ? (totalRating / ratingCount).toFixed(1) : null
          });
        } catch (error) {
          console.error("Error fetching venues:", error);
        }
      };
      fetchVenues();
    }
  }, [cityInfo, citySlug]);

  const filteredVenues = selectedType 
    ? venues.filter(v => v.venue_type === selectedType)
    : venues;

  const cityName = cityInfo?.name || FALLBACK_CITIES[citySlug?.toLowerCase()]?.name || (citySlug ? citySlug.charAt(0).toUpperCase() + citySlug.slice(1) : '');

  // Show city index page
  if (showCityIndex) {
    return (
      <div className="min-h-screen bg-surface">
        <SEO 
          title="Find Remote Workspaces by City"
          description="Discover the best cafes, hotels, libraries and coworking spaces for remote work in cities across the UK."
          canonical="/city"
        />
        <Header />
        <main className="pt-20 pb-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Find Remote Workspaces by City
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Explore laptop-friendly cafes, coworking spaces, and more in cities across the UK.
            </p>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-brand" />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {allCities.map((city) => (
                  <Link
                    key={city.slug}
                    to={`/city/${city.slug}`}
                    className="group p-4 bg-white rounded-xl border border-gray-200 hover:border-brand hover:shadow-lg transition-all"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-5 h-5 text-brand" />
                      <h2 className="font-semibold text-gray-900 group-hover:text-brand transition-colors">
                        {city.name}
                      </h2>
                    </div>
                    <p className="text-sm text-gray-500 line-clamp-2">{city.description}</p>
                    {city.variations && city.variations.length > 0 && (
                      <p className="text-xs text-gray-400 mt-2">
                        Also: {city.variations.slice(0, 2).join(", ")}
                      </p>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // City not found
  if (!loading && !cityInfo && !FALLBACK_CITIES[citySlug?.toLowerCase()]) {
    return (
      <div className="min-h-screen bg-surface">
        <SEO title="City Not Found" canonical={`/city/${citySlug}`} />
        <Header />
        <main className="pt-20 pb-16 px-4">
          <div className="max-w-6xl mx-auto text-center py-16">
            <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">City Not Found</h1>
            <p className="text-gray-500 mb-6">We couldn't find "{citySlug}". Try browsing all cities instead.</p>
            <Link to="/city" className="btn-primary">View All Cities</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const displayCityInfo = cityInfo || FALLBACK_CITIES[citySlug?.toLowerCase()] || {};

  return (
    <div className="min-h-screen bg-surface">
      <SEO 
        title={`Best Places to Work Remotely in ${cityName} - Cafes, Coworking & More`}
        description={`Find the best cafes, hotels, libraries and coworking spaces with WiFi and power outlets in ${cityName}. ${stats?.total || 'Many'} laptop-friendly venues for remote workers.`}
        keywords={`remote work ${cityName}, coworking ${cityName}, laptop friendly cafe ${cityName}, WiFi cafe ${cityName}`}
        canonical={`/city/${citySlug}`}
      />
      <BreadcrumbStructuredData items={[
        { name: "Home", path: "/" },
        { name: "Cities", path: "/city" },
        { name: cityName, path: `/city/${citySlug}` }
      ]} />
      
      <Header />

      {/* Hero Section */}
      <section className="pt-20 pb-8 bg-gradient-to-br from-brand/5 via-white to-blue-50">
        <div className="max-w-6xl mx-auto px-4">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-brand">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/city" className="hover:text-brand">Cities</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900">{cityName}</span>
          </nav>

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Best Places to Work Remotely in {cityName}
              </h1>
              <p className="text-lg text-gray-600 mb-6">
                {displayCityInfo.description || `Discover laptop-friendly cafes, coworking spaces, and more in ${cityName}.`}
              </p>

              {/* Stats */}
              {stats && (
                <div className="flex flex-wrap gap-4 mb-6">
                  <div className="bg-white rounded-xl px-4 py-3 shadow-sm border border-gray-100">
                    <div className="text-2xl font-bold text-brand">{stats.total}</div>
                    <div className="text-sm text-gray-500">Workspaces</div>
                  </div>
                  {stats.avgRating && (
                    <div className="bg-white rounded-xl px-4 py-3 shadow-sm border border-gray-100">
                      <div className="flex items-center gap-1 text-2xl font-bold text-amber-500">
                        <Star className="w-5 h-5 fill-current" />
                        {stats.avgRating}
                      </div>
                      <div className="text-sm text-gray-500">Avg Rating</div>
                    </div>
                  )}
                  <div className="bg-white rounded-xl px-4 py-3 shadow-sm border border-gray-100">
                    <div className="text-2xl font-bold text-green-600">{Object.keys(stats.byType).length}</div>
                    <div className="text-sm text-gray-500">Venue Types</div>
                  </div>
                </div>
              )}

              {/* Highlights */}
              {displayCityInfo.highlights && displayCityInfo.highlights.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {displayCityInfo.highlights.map((highlight) => (
                    <span key={highlight} className="px-3 py-1 bg-brand/10 text-brand text-sm rounded-full">
                      {highlight}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Mini Map */}
            {displayCityInfo.lat && displayCityInfo.lng && (
              <div className="lg:w-96 h-64 lg:h-auto rounded-xl overflow-hidden shadow-lg border border-gray-200">
                <VenueMap
                  venues={venues.slice(0, 20)}
                  center={[displayCityInfo.lat, displayCityInfo.lng]}
                  zoom={12}
                  height="100%"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Type Filter & Content */}
      <main className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Laptop-Friendly Venues in {cityName}
          </h2>

          {/* Type Filter Pills */}
          {stats?.byType && Object.keys(stats.byType).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              <button
                onClick={() => setSelectedType(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  !selectedType ? 'bg-brand text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All ({stats.total})
              </button>
              {Object.entries(stats.byType).map(([type, count]) => {
                const Icon = venueTypeIcons[type] || Building2;
                return (
                  <button
                    key={type}
                    onClick={() => setSelectedType(type === selectedType ? null : type)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedType === type ? 'bg-brand text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{venueTypePlurals[type] || `${type}s`}</span>
                    <span className="opacity-70">({count})</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Venues Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={`skeleton-${i}`} className="bg-white rounded-xl h-80 animate-pulse" />
              ))}
            </div>
          ) : filteredVenues.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredVenues.map((venue, index) => (
                  <motion.div
                    key={venue.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <VenueCard venue={venue} />
                  </motion.div>
                ))}
              </div>

              {/* SEO Content */}
              <section className="mt-16">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Why Work Remotely in {cityName}?
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {cityName} has become a top destination for remote workers and digital nomads in the UK. 
                  With {stats?.total || 'numerous'} laptop-friendly venues ranging from cosy cafes to professional 
                  coworking spaces, you'll always find the perfect spot to be productive.
                </p>
              </section>

              <section className="mt-12">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Tips for Remote Working in {cityName}</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Wifi className="w-5 h-5 text-blue-600" />
                      </div>
                      <h4 className="font-semibold text-gray-900">Check WiFi Speed</h4>
                    </div>
                    <p className="text-gray-600 text-sm">Look for venues marked "Very Fast" or "Fast" WiFi for video calls.</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <Plug className="w-5 h-5 text-green-600" />
                      </div>
                      <h4 className="font-semibold text-gray-900">Power Outlets Matter</h4>
                    </div>
                    <p className="text-gray-600 text-sm">Filter for venues with "Plenty" or "Many" power outlets.</p>
                  </div>
                </div>
              </section>
            </>
          ) : (
            <div className="text-center py-16">
              <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No venues found in {cityName}</h3>
              <p className="text-gray-500 mb-6">Be the first to add a workspace in this area!</p>
              <Link to="/add-location" className="inline-flex items-center gap-2 btn-primary">
                Add a Location <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {/* Browse All CTA */}
          <div className="mt-12 text-center">
            <Link to={`/browse?city=${cityName}`} className="inline-flex items-center gap-2 text-brand hover:text-brand-dark font-medium">
              Browse all workspaces in {cityName} with filters <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Other Cities */}
          <section className="mt-16 pt-8 border-t border-gray-200">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Explore Other Cities</h3>
            <div className="flex flex-wrap gap-3">
              {otherCities.map(([slug, city]) => (
                  <Link
                    key={slug}
                    to={`/city/${slug}`}
                    className="px-4 py-2 bg-gray-100 hover:bg-brand hover:text-white rounded-full text-sm font-medium text-gray-700 transition-colors"
                  >
                    {city.name}
                  </Link>
                ))}
              <Link to="/city" className="px-4 py-2 bg-brand/10 text-brand hover:bg-brand hover:text-white rounded-full text-sm font-medium transition-colors">
                View All Cities →
              </Link>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
